import dynamic from 'next/dynamic';
import React from 'react';

// components
import { Box, Section } from 'src/components-v2/Layout';
import { MainNavigationBar } from 'src/components-v2/MainNavigationBar';
import FooterV3 from 'src/components-v3/Footer';
import { ContentfulToolbar } from 'src/components/ContentfulToolbar';
import { UnidentifiedTraffic } from 'src/components/Feedback';
import Footer from 'src/components/footer';
import { MetaTags } from 'src/components/MetaTags';
import { MainNav } from 'src/components/navigation';
import { ConsentManager } from 'src/containers/ConsentManager';
import { CmsThemeContextProvider } from 'src/contexts/CmsThemeContext/CmsThemeContextProvider';
import { BotCheckEvent } from 'src/hooks/useBotCheckEvent';
import { PerformanceProvider } from 'src/hooks/useIsPotato';
import { MainMenu } from 'src/sections/Navigation';

// helpers
import { AnalyticsProvider, LocaleContext } from 'src/contexts';
import { CmsThemes, Locales, type TLocale } from 'src/lib/constants';
import type SxProps from 'threads5/themes/sx-props';
import type { ITheme } from 'threads5/themes/theme';

// scripts
import initAnalytics from 'public/assets/js/analytics';
import 'public/assets/js/analytics/utilities';

const ForeignContentNotification = dynamic(
  () => {
    return import(
      'src/components/Feedback/ForeignContentNotification/ForeignContentNotification'
    );
  },
  {
    ssr: false,
  },
);

// For both, new static and new CMS pages (using in Page template)
const MainTemplate = ({
  children,
  locale = Locales.EN_US,
  hasForeignContentNotification = true,
  hasFooter = true,
  hasNavigation = true,
  cmsTheme = CmsThemes.WEB2,
  sx = {},
  ...props
}: {
  children: React.ReactNode;
  locale?: TLocale;
  hasForeignContentNotification?: boolean;
  hasFooter?: boolean;
  hasNavigation?: boolean;
  cmsTheme?: string;
  sx?: SxProps<ITheme>;
  [K: string]: unknown;
}) => {
  const isWeb3 = cmsTheme === CmsThemes.WEB3;

  React.useEffect(() => {
    initAnalytics();
  }, []);

  /*
   * NOTE
   * ---------------------------------------------------------------------------
   * Moving forward all context providers should be added to the _app.tsx file.
   * This is to ensure that the context providers are available for the entire
   * application and not just the current page.
   *
   */
  return (
    <CmsThemeContextProvider cmsTheme={cmsTheme}>
      <LocaleContext.Provider value={locale || Locales.EN_US}>
        <AnalyticsProvider>
          <BotCheckEvent />
          <PerformanceProvider>
            {/* @ts-ignore */}
            <MetaTags {...props} />
            <Box
              sx={{
                bgcolor: 'white',
                ...sx,
              }}
            >
              {hasNavigation &&
                (isWeb3 ? (
                  <>
                    <MainNavigationBar />
                    {hasForeignContentNotification && (
                      <ForeignContentNotification hasFloatingNav />
                    )}
                  </>
                ) : locale === Locales.EN_GB || locale === Locales.EN_EU ? (
                  <>
                    <MainNav {...props} />
                    {hasForeignContentNotification && (
                      <ForeignContentNotification />
                    )}
                  </>
                ) : (
                  <MainMenu />
                ))}

              <Section component='main' id='main-content'>
                {children}
              </Section>
            </Box>
            {hasFooter &&
              (isWeb3 ? (
                <FooterV3 />
              ) : (
                /* @ts-ignore */
                <Footer {...props} />
              ))}
            <ConsentManager />
            <UnidentifiedTraffic />
            <ContentfulToolbar />
          </PerformanceProvider>
        </AnalyticsProvider>
      </LocaleContext.Provider>
    </CmsThemeContextProvider>
  );
};

export default MainTemplate;
